import React from 'react';
import CTA from '../components/CTA';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import ServiceBlogSection from '../components/ServiceBlogSection';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const DeckCleaning = () => {
  const serviceSEO = {
    title: 'Deck Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional deck cleaning in Phoenix, AZ. Restore your deck\'s beauty, remove stains & mildew. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Deck Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Deck Cleaning'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Deck Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional deck cleaning in Phoenix, AZ. Restore your deck's beauty, remove stains & mildew. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="wood deck restoration, composite deck cleaning, deck stain removal, Phoenix deck maintenance, deck mildew treatment, outdoor living space cleaning, deck sealing preparation, deck power washing, deck surface restoration, deck safety cleaning"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Deck Cleaning Banner">
        <picture>
          <source srcSet="/patio_deck_hero.avif" type="image/avif" />
          <source srcSet="/patio_deck_hero.webp" type="image/webp" />
        <img 
          src="/patio_deck_hero.png" 
          alt="Professional deck cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Deck Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Deck Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          At Copper Canyon Pressure Wash, we provide professional deck cleaning services for homeowners in the greater Phoenix area, based in Scottsdale. Decks are exposed to Arizona's intense sun, dust, and occasional monsoon rains, leading to dirt buildup, fading, and mildew growth. Our team uses specialized pressure washing techniques to restore your deck's beauty and durability, making it a safe and enjoyable space for outdoor living.
        </p>
        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Slip-Free Decks</h3>
            </div>
            <p className="text-gray-700">We remove dirt and mildew that can make your deck slippery, ensuring a safer surface for family gatherings.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Color Restoration</h3>
            </div>
            <p className="text-gray-700">Our cleaning process revives faded wood or composite decks, bringing back their natural color and charm.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Eco-Friendly Cleaning</h3>
            </div>
            <p className="text-gray-700">We use eco-friendly cleaners to break down grime without harming your deck's finish or surrounding plants.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Outdoor Enjoyment</h3>
            </div>
            <p className="text-gray-700">A well-maintained deck enhances your outdoor space, adding value and appeal to your home year-round.</p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Deck Cleaning Process</h2>
        <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Inspection</h3>
                <p className="text-gray-700">We start by inspecting your deck for dirt, mildew, or sun damage, noting any areas needing extra care.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Biodegradable Pre-Treatment</h3>
                <p className="text-gray-700">We apply biodegradable detergents to loosen grime and treat organic growth like mildew or algae.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Gentle or Moderate Cleaning</h3>
                <p className="text-gray-700">Using adjustable pressure washers, we clean with a low setting for wood decks to avoid splintering, or a moderate setting for composite materials.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Rinse & Inspection</h3>
                <p className="text-gray-700">We finish with a thorough rinse and inspection, ensuring your deck is clean, safe, and ready for your next outdoor event.</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Deck Cleaning</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Precise Pressure Settings</h3>
            <p className="text-gray-700">We use precise pressure settings—like 800 PSI for wood or 1,500 PSI for composite—to prevent damage like splintering or surface etching.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">All Deck Materials</h3>
            <p className="text-gray-700">We're experienced with all deck materials, including cedar, pressure-treated wood, and composite decking, ensuring proper care for each.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
            <p className="text-gray-700">Our expertise avoids issues like raised grain or damaged finishes, which can occur with inexperienced cleaners using improper techniques.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Arizona Deck Know-How</h3>
            <p className="text-gray-700">We understand how Arizona's climate affects decks, delivering a clean that protects against sun fading and moisture damage.</p>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Deck Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our deck cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ
        items={[
          {
            question: 'How often should I have my deck cleaned?',
            answer: 'We recommend cleaning your deck at least once a year to prevent buildup of dirt, mold, and algae. Regular cleaning helps maintain its appearance and extends its lifespan.'
          },
          {
            question: 'Is your cleaning process safe for wood and composite decks?',
            answer: 'Yes, we use gentle cleaning methods and solutions that are safe for both wood and composite decking. Our technicians adjust the pressure and cleaning agents to suit your deck material.'
          },
          {
            question: 'Can you remove stains and slippery spots from my deck?',
            answer: 'Absolutely! Our cleaning process is designed to remove stains, algae, and slippery spots, making your deck safer and more attractive.'
          }
        ]}
        title="Frequently Asked Questions About Deck Cleaning"
      />
      <ServiceBlogSection blogSlugs={['pressure-washing-composite-deck-warranty']} />
    </div>
  );
};

export default DeckCleaning; 